import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  clientTypes, provinces, turnoverBands, serviceOptions, yearsOperatingOptions,
  urgencyOptions, contactPreferences, recordsStatusOptions, industries, site,
} from "@/config/site";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getAttribution } from "@/hooks/useAttribution";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { openWhatsApp } from "@/lib/whatsapp";
import { toast } from "sonner";

const TOTAL = 10;

function OptionCard({ label, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center justify-between rounded-xl border-2 px-4 py-3.5 text-left text-sm font-medium transition-all ${
        selected
          ? "border-[#133827] bg-[#133827]/5 text-[#133827] ring-2 ring-[#133827]/15"
          : "border-slate-200 bg-white text-[#526057] hover:border-[#2C5E43]/50"
      }`}
    >
      <span>{label}</span>
      {selected && <Check className="h-4 w-4 flex-shrink-0 text-[#2C5E43]" />}
    </button>
  );
}

function StepShell({ title, subtitle, children }) {
  return (
    <div className="animate-fade-up">
      <h2 className="font-display text-2xl font-bold text-[#18201B] sm:text-3xl">{title}</h2>
      {subtitle && <p className="mt-2 text-sm text-[#526057]">{subtitle}</p>}
      <div className="mt-7">{children}</div>
    </div>
  );
}

export default function Assessment() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [data, setData] = useState({
    first_name: "", surname: "", phone: "", email: "",
    client_type: "", business_name: "", industry: "", province: "", years_operating: "",
    turnover_band: "", services_required: [],
    has_accountant: "", records_status: "",
    urgency: "", preferred_contact: "", preferred_time: "",
    additional_notes: "", consent: false,
  });

  // Preselect industry from campaign landing page
  useEffect(() => {
    const ind = params.get("industry");
    if (ind) setData((d) => ({ ...d, industry: ind }));
    trackEvent(EVENTS.START_ASSESSMENT, { industry: params.get("industry") || null });
  }, [params]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const toggleService = (s) =>
    setData((d) => ({
      ...d,
      services_required: d.services_required.includes(s)
        ? d.services_required.filter((x) => x !== s)
        : [...d.services_required, s],
    }));

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);

  const canNext = useMemo(() => {
    switch (step) {
      case 1: return data.first_name.trim() && data.surname.trim() && data.phone.trim() && emailValid;
      case 2: return !!data.client_type;
      case 3: return true; // business details optional for individuals
      case 4: return !!data.turnover_band;
      case 5: return data.services_required.length > 0;
      case 6: return !!data.has_accountant && !!data.records_status;
      case 7: return !!data.urgency;
      case 8: return !!data.preferred_contact;
      case 9: return true;
      case 10: return data.consent;
      default: return false;
    }
  }, [step, data, emailValid]);

  const next = () => {
    if (!canNext) {
      toast.error("Please complete this step to continue.");
      return;
    }
    if (step < TOTAL) setStep(step + 1);
  };
  const back = () => step > 1 && setStep(step - 1);

  const submit = async () => {
    if (!data.consent) { toast.error("Consent is required to submit."); return; }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/leads", { ...data, attribution: getAttribution() });
      trackEvent(EVENTS.COMPLETE_ASSESSMENT, { ref: res.lead_ref });
      trackEvent(EVENTS.LEAD, { ref: res.lead_ref, from: "assessment" });
      setDone(res.lead_ref);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <Seo title="Thank you" path="/assessment" />
        <section className="flex min-h-[70vh] items-center justify-center px-4 py-16">
          <div className="animate-fade-up max-w-lg rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="assessment-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#2C5E43]/10">
              <CheckCircle2 className="h-9 w-9 text-[#2C5E43]" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-bold text-[#18201B]">Thank you.</h1>
            <p className="mt-3 text-[#526057]">
              Your enquiry has been received. Bonsai Financial will review the information you provided
              and contact you using your preferred contact method.
            </p>
            <p className="mt-3 text-sm text-[#526057]">Your reference: <span className="font-mono font-semibold text-[#133827]">{done}</span></p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button onClick={() => openWhatsApp("consultation")} data-testid="success-whatsapp-btn" className="bg-[#25D366] text-white hover:bg-[#20ba5a]">
                <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp Bonsai
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} data-testid="success-home-btn">Back to Home</Button>
            </div>
          </div>
        </section>
      </>
    );
  }

  const pct = Math.round((step / TOTAL) * 100);

  return (
    <>
      <Seo title="Request a Consultation" description="Complete Bonsai Financial's short consultation assessment so we can understand your needs and arrange the right support." path="/assessment" />
      <section className="bg-[#F9F8F5] py-10 sm:py-14">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-[#133827]">Step {step} of {TOTAL}</span>
              <span className="text-[#526057]">{pct}%</span>
            </div>
            <Progress value={pct} className="mt-2 h-2" data-testid="assessment-progress" />
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            {step === 1 && (
              <StepShell title="Tell us who you are" subtitle="So Bonsai can get in touch with you.">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label htmlFor="fn">First name</Label><Input id="fn" data-testid="input-first-name" value={data.first_name} onChange={(e) => set({ first_name: e.target.value })} className="mt-1.5" /></div>
                  <div><Label htmlFor="sn">Surname</Label><Input id="sn" data-testid="input-surname" value={data.surname} onChange={(e) => set({ surname: e.target.value })} className="mt-1.5" /></div>
                  <div><Label htmlFor="ph">Mobile number</Label><Input id="ph" data-testid="input-phone" value={data.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="e.g. 073 000 0000" className="mt-1.5" /></div>
                  <div>
                    <Label htmlFor="em">Email address</Label>
                    <Input id="em" type="email" data-testid="input-email" value={data.email} onChange={(e) => set({ email: e.target.value })} className="mt-1.5" />
                    {data.email && !emailValid && <p className="mt-1 text-xs text-destructive">Please enter a valid email.</p>}
                  </div>
                </div>
              </StepShell>
            )}

            {step === 2 && (
              <StepShell title="What best describes you?" subtitle="Choose the option that fits your situation.">
                <div className="grid gap-3 sm:grid-cols-2">
                  {clientTypes.map((t) => (
                    <OptionCard key={t} label={t} selected={data.client_type === t} onClick={() => set({ client_type: t })} testId={`client-type-${t.toLowerCase().replace(/\s+/g, "-")}`} />
                  ))}
                </div>
              </StepShell>
            )}

            {step === 3 && (
              <StepShell title="Your business details" subtitle="Skip anything that doesn't apply to you.">
                <div className="grid gap-4">
                  <div><Label htmlFor="bn">Business name</Label><Input id="bn" data-testid="input-business-name" value={data.business_name} onChange={(e) => set({ business_name: e.target.value })} className="mt-1.5" /></div>
                  <div>
                    <Label htmlFor="ind">Industry / profession</Label>
                    <select id="ind" data-testid="select-industry" value={data.industry} onChange={(e) => set({ industry: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                      <option value="">Select or leave blank</option>
                      {industries.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="prov">Province</Label>
                      <select id="prov" data-testid="select-province" value={data.province} onChange={(e) => set({ province: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                        <option value="">Select</option>
                        {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="yrs">Years operating</Label>
                      <select id="yrs" data-testid="select-years" value={data.years_operating} onChange={(e) => set({ years_operating: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                        <option value="">Select</option>
                        {yearsOperatingOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </StepShell>
            )}

            {step === 4 && (
              <StepShell title="Approximate annual turnover" subtitle="A rough range is perfectly fine.">
                <div className="grid gap-3 sm:grid-cols-2">
                  {turnoverBands.map((t) => (
                    <OptionCard key={t} label={t} selected={data.turnover_band === t} onClick={() => set({ turnover_band: t })} testId={`turnover-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                  ))}
                </div>
              </StepShell>
            )}

            {step === 5 && (
              <StepShell title="Which services are you interested in?" subtitle="Select all that apply — or choose 'Not Sure'.">
                <div className="grid gap-3 sm:grid-cols-2">
                  {serviceOptions.map((s) => (
                    <OptionCard key={s} label={s} selected={data.services_required.includes(s)} onClick={() => toggleService(s)} testId={`service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                  ))}
                </div>
              </StepShell>
            )}

            {step === 6 && (
              <StepShell title="Your current accounting situation">
                <div>
                  <Label className="text-sm font-semibold text-[#18201B]">Do you currently have an accountant or bookkeeper?</Label>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {["Yes", "No"].map((o) => (
                      <OptionCard key={o} label={o} selected={data.has_accountant === o} onClick={() => set({ has_accountant: o })} testId={`has-accountant-${o.toLowerCase()}`} />
                    ))}
                  </div>
                </div>
                <div className="mt-6">
                  <Label className="text-sm font-semibold text-[#18201B]">Are your accounting records currently up to date?</Label>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {recordsStatusOptions.map((o) => (
                      <OptionCard key={o} label={o} selected={data.records_status === o} onClick={() => set({ records_status: o })} testId={`records-${o.toLowerCase().replace(/\s+/g, "-")}`} />
                    ))}
                  </div>
                </div>
              </StepShell>
            )}

            {step === 7 && (
              <StepShell title="How soon do you need support?">
                <div className="grid gap-3 sm:grid-cols-2">
                  {urgencyOptions.map((u) => (
                    <OptionCard key={u} label={u} selected={data.urgency === u} onClick={() => set({ urgency: u })} testId={`urgency-${u.toLowerCase().replace(/\s+/g, "-")}`} />
                  ))}
                </div>
              </StepShell>
            )}

            {step === 8 && (
              <StepShell title="How would you prefer we contact you?">
                <div className="grid gap-3 sm:grid-cols-3">
                  {contactPreferences.map((c) => (
                    <OptionCard key={c} label={c} selected={data.preferred_contact === c} onClick={() => set({ preferred_contact: c })} testId={`contact-pref-${c.toLowerCase()}`} />
                  ))}
                </div>
                <div className="mt-6">
                  <Label htmlFor="pt">Preferred consultation day / time (optional)</Label>
                  <Input id="pt" data-testid="input-preferred-time" value={data.preferred_time} onChange={(e) => set({ preferred_time: e.target.value })} placeholder="e.g. Weekday mornings, or Tuesday afternoon" className="mt-1.5" />
                </div>
              </StepShell>
            )}

            {step === 9 && (
              <StepShell title="Anything else we should know?" subtitle="Optional — share any detail that will help us prepare.">
                <Textarea rows={5} data-testid="input-additional-notes" value={data.additional_notes} onChange={(e) => set({ additional_notes: e.target.value })} placeholder="Tell us a little more about your situation…" />
              </StepShell>
            )}

            {step === 10 && (
              <StepShell title="Consent & submission" subtitle="One last step before we receive your enquiry.">
                <div className="rounded-xl bg-[#F9F8F5] p-5">
                  <div className="flex items-start gap-3">
                    <Checkbox id="consent" checked={data.consent} data-testid="assessment-consent-checkbox" onCheckedChange={(v) => set({ consent: !!v })} className="mt-1" />
                    <Label htmlFor="consent" className="text-sm font-normal leading-relaxed text-[#526057]">
                      I consent to Bonsai Financial contacting me regarding my enquiry. I understand that
                      submitting this form does not by itself create a professional engagement. See our{" "}
                      <a href="/privacy" target="_blank" rel="noreferrer" className="font-medium text-[#2C5E43] underline">Privacy Policy</a>.
                    </Label>
                  </div>
                </div>
                <p className="mt-4 text-xs text-[#526057]">Contact: {site.phoneDisplay} · {site.email}</p>
              </StepShell>
            )}

            {/* Controls */}
            <div className="mt-10 flex items-center justify-between gap-3">
              <Button variant="ghost" onClick={back} disabled={step === 1} data-testid="assessment-back-btn" className="text-[#526057]">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
              </Button>
              {step < TOTAL ? (
                <Button onClick={next} data-testid="assessment-next-btn" className="bg-[#133827] px-6 text-white hover:bg-[#0d261a]">
                  Next <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={submitting || !data.consent} data-testid="assessment-submit-btn" className="bg-[#C68B59] px-6 text-white hover:bg-[#b2794a]">
                  {submitting ? "Submitting…" : "Submit Enquiry"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
